import React, { useMemo, useState } from "react";
import axios from "axios";
import Links from "../../Data/Links";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const SalesFunnelUpload = () => {
  const now = useMemo(() => new Date(), []);
  const [file, setFile] = useState(null);
  const [month, setMonth] = useState(monthNames[now.getMonth()]);
  const [year, setYear] = useState(String(now.getFullYear()));
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [uploadResults, setUploadResults] = useState(null);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    const fileExtension = selectedFile?.name.split(".").pop().toLowerCase();
    if (selectedFile && fileExtension === "csv") {
      setFile(selectedFile);
      setMessage({ type: "", text: "" });
      setUploadResults(null);
    } else {
      setMessage({ type: "error", text: "Please select a CSV file" });
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage({ type: "error", text: "Please select a CSV file" });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });
    setUploadResults(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("month", month);
      formData.append("year", year);

      const response = await axios.post(Links.uploadFunnelData, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setUploadResults({
        total: response.data?.totalRecords ?? 0,
        success: response.data?.successCount ?? 0,
        failed: response.data?.failedCount ?? 0,
        errors: response.data?.errors ?? [],
      });

      setMessage({
        type: "success",
        text: `Uploaded ${response.data?.successCount ?? 0} of ${response.data?.totalRecords ?? 0} rows`,
      });
      setFile(null);
      const input = document.getElementById("sales-funnel-file-input");
      if (input) input.value = "";
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || err.message || "Upload failed",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ margin: "2rem" }}>
      <h2 style={{ marginBottom: "1rem" }}>Socket Data Upload</h2>
      <p style={{ marginBottom: "1.5rem", color: "#555" }}>
        Upload the Dealer Socket export in CSV format; it will be transformed and written to the database.
      </p>

      <div style={{
        padding: "1.5rem",
        backgroundColor: "#f5f5f5",
        borderRadius: "8px",
        marginBottom: "2rem",
      }}>
        <h4 style={{ marginBottom: "1rem" }}>Upload CSV</h4>

        <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1rem" }}>
          <input
            id="sales-funnel-file-input"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          <label htmlFor="sales-funnel-file-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
              size="large"
            >
              Choose CSV
            </Button>
          </label>
          {file && (
            <div style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#e3f2fd",
              borderRadius: "4px",
              fontWeight: 500,
            }}>
              {file.name}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: "1rem", marginBottom: "1.5rem" }}>
          <Box sx={{ minWidth: 160 }}>
            <FormControl fullWidth>
              <InputLabel id="month-label">Month</InputLabel>
              <Select
                labelId="month-label"
                value={month}
                label="Month"
                onChange={(e) => setMonth(e.target.value)}
              >
                {monthNames.map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 140 }}>
            <FormControl fullWidth>
              <InputLabel id="year-label">Year</InputLabel>
              <Select
                labelId="year-label"
                value={year}
                label="Year"
                onChange={(e) => setYear(e.target.value)}
              >
                {[String(now.getFullYear() - 1), String(now.getFullYear()), String(now.getFullYear() + 1)].map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </div>

        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          disabled={!file || loading}
          size="large"
          style={{ minWidth: "160px" }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : "Upload"}
        </Button>

        {message.text && (
          <div style={{ marginTop: "1rem" }}>
            <Alert
              severity={message.type}
              onClose={() => setMessage({ type: "", text: "" })}
            >
              {message.text}
            </Alert>
          </div>
        )}

        {uploadResults && (
          <div style={{
            marginTop: "1rem",
            padding: "1rem",
            backgroundColor: "#fff",
            borderRadius: "4px",
            border: "1px solid #ddd",
          }}>
            <h5>Upload Results</h5>
            <div>Total Rows: <strong>{uploadResults.total}</strong></div>
            <div style={{ color: "green" }}>Success: <strong>{uploadResults.success}</strong></div>
            <div style={{ color: "red" }}>Failed: <strong>{uploadResults.failed}</strong></div>
            {uploadResults.errors?.length > 0 && (
              <ul style={{ marginTop: "0.5rem" }}>
                {uploadResults.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div style={{
          marginTop: "1rem",
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          padding: "1rem",
          color: "#555",
          fontSize: "0.95rem",
        }}>
          <strong>Notes:</strong>
          <ul style={{ margin: "0.5rem 0 0 1rem" }}>
            <li>CSV only; the backend handles transformation and database write.</li>
            <li>Month/Year defaults to the current month; adjust if you are loading past periods.</li>
            <li>Skipped stores and store ID mapping follow the server transform rules.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SalesFunnelUpload;
